import { useState } from 'react'
import { Button } from '@/app/common/components/button/Button'
import { SubmitButton } from '@/common/components/SubmitButton.tsx'
import { useAppAlert } from '@/layouts/AppAlertProvider.tsx'
import { useImportProvidersToAffiliation } from '@/queries/providers/affiliations/hooks.ts'
import { DOWNLOADAffiliationProvidersImportTemplate } from '@/queries/providers/affiliations/requests.ts'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shad/ui/dialog.tsx'
import AffiliationProvidersFileUpload from './AffiliationProvidersFileUpload.tsx'
import { RequestError } from '@/common/types_and_interfaces.ts'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  affiliationId: number
  affiliationName: string
  programId: number
}

export default function BulkUploadProvidersDialog({
  open,
  onOpenChange,
  affiliationId,
  affiliationName,
  programId,
}: Props) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const { fireAlert } = useAppAlert()

  const importProvidersMutation = useImportProvidersToAffiliation({
    onSuccess: async (result) => {
      await fireAlert({
        variant: 'success',
        titleText: 'Providers Imported',
        messageComponent: (
          <div className="space-y-1 text-sm">
            <p>
              <span className="font-semibold">{result.inserted_count}</span>{' '}
              provider(s) added to {affiliationName}.
            </p>
            <p>
              <span className="font-semibold">
                {result.already_attached_count}
              </span>{' '}
              provider(s) were already previously added to {affiliationName}.
            </p>
          </div>
        ),
      })

      setSelectedFile(null)
      onOpenChange(false)
    },
    onError: async (error) => {
      await fireAlert({
        variant: 'error',
        titleText: 'Import Failed',
        message: error.message,
      })
    },
  })

  const downloadTemplate = async () => {
    try {
      const response = await DOWNLOADAffiliationProvidersImportTemplate()
      const fileURL = URL.createObjectURL(response.data)
      const link = document.createElement('a')
      link.href = fileURL
      link.download = 'affiliation-provider-import-template.csv'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(fileURL)
    } catch (error) {
      const requestError = error as RequestError

      await fireAlert({
        variant: 'error',
        titleText: 'Download Failed',
        message:
          requestError?.message ||
          'There was an error downloading the import template.',
      })
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (importProvidersMutation.isPending && !nextOpen) {
          return
        }

        if (!nextOpen) {
          setSelectedFile(null)
        }

        onOpenChange(nextOpen)
      }}
    >
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Bulk Upload Providers</DialogTitle>
          <DialogDescription>
            Upload providers to {affiliationName} using a CSV template.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <AffiliationProvidersFileUpload
            disabled={importProvidersMutation.isPending}
            onFileChange={setSelectedFile}
          />

          <div className="rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-gray-600">
            <p className="font-semibold text-gray-800">File Requirements:</p>
            <ul className="mt-1 list-disc space-y-1 pl-5">
              <li>CSV File Format</li>
              <li>Required field header: provider_id</li>
              <li>Maximum 1000 records per upload</li>
              <li>Maximum file size 2 MB</li>
            </ul>
            <div className="pt-2">
              <Button
                type="button"
                variant="transparent"
                size="sm"
                className="h-auto p-0 text-sm font-medium text-primary underline underline-offset-2"
                disabled={importProvidersMutation.isPending}
                onClick={() => {
                  void downloadTemplate()
                }}
              >
                Download Import Template
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:justify-end">
          <Button
            type="button"
            variant="outline"
            disabled={importProvidersMutation.isPending}
            onClick={() => {
              onOpenChange(false)
            }}
          >
            Cancel
          </Button>

          <div>
            <SubmitButton
              type="button"
              status={importProvidersMutation.status}
              buttonTxt="Upload Providers"
              submittingText="Uploading..."
              forceDisabled={!selectedFile}
              onClick={() => {
                if (!selectedFile) {
                  return
                }

                importProvidersMutation.mutate({
                  affiliationId,
                  programId,
                  file: selectedFile,
                })
              }}
            />
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
